// Utilidades compartilhadas entre a aba Faturas (titular) e o Faturamento (master).
import Swal from 'sweetalert2';

export const STATUS_PAGOS = ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'];
export const STATUS_ABERTOS = ['PENDING', 'OVERDUE'];

const TEXTO_STATUS = {
  PENDING: 'Pendente',
  RECEIVED: 'Pago',
  CONFIRMED: 'Confirmado',
  RECEIVED_IN_CASH: 'Pago em dinheiro',
  OVERDUE: 'Vencido',
  REFUNDED: 'Reembolsado',
  CANCELLED: 'Cancelada',
  ERROR: 'Erro na geração',
  PROCESSING: 'Processando'
};

const CLASSE_STATUS = {
  PENDING: 'lancado',
  RECEIVED: 'ativo',
  CONFIRMED: 'ativo',
  RECEIVED_IN_CASH: 'ativo',
  OVERDUE: 'inativo',
  REFUNDED: 'bloqueado',
  CANCELLED: 'bloqueado',
  ERROR: 'bloqueado',
  PROCESSING: 'lancado'
};

export const textoStatusFatura = status => TEXTO_STATUS[status] || status || '-';
export const classeStatusFatura = status => CLASSE_STATUS[status] || 'lancado';

export const formatarCompetencia = mes =>
  (/^\d{4}-\d{2}$/.test(mes || '') ? `${mes.slice(5)}/${mes.slice(0, 4)}` : mes || '-');

export const moedaBRL = valor =>
  Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const erroDaFatura = fatura => {
  if (fatura.status !== 'ERROR' || !fatura.asaas_data) return '';
  try {
    const dados = typeof fatura.asaas_data === 'string' ? JSON.parse(fatura.asaas_data) : fatura.asaas_data;
    return dados?.erro || '';
  } catch (e) {
    return '';
  }
};

// Competência fechada mais recente (mês anterior ao atual), no formato YYYY-MM.
export const competenciaAnterior = () => {
  const hoje = new Date();
  const data = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
};

export const linkWhatsapp = telefone => {
  const digitos = String(telefone || '').replace(/\D/g, '');
  if (digitos.length < 10) return null;
  return `https://wa.me/${digitos.startsWith('55') ? digitos : `55${digitos}`}`;
};

export const formatarTelefone = telefone => {
  const d = String(telefone || '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return telefone || '';
};

// Abre o boleto/fatura do Asaas em nova aba.
export const urlBoleto = fatura => fatura.bank_slip_url || fatura.boleto_url || fatura.invoice_url || null;

export const abrirBoletoFatura = fatura => {
  const url = urlBoleto(fatura);
  if (url) window.open(url, '_blank', 'noopener');
  else Swal.fire('Aviso', 'Boleto não disponível para esta fatura', 'warning');
};

const escaparHtml = texto => String(texto ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export const abrirPixFatura = async (api, fatura) => {
  try {
    const response = await api.get(`/faturas/${fatura.id}/pix`);
    if (!response || !response.payload) {
      Swal.fire('Aviso', 'PIX não disponível para esta fatura', 'warning');
      return;
    }
    Swal.fire({
      title: 'PIX Copia e Cola',
      html: `
        <div style="margin: 20px 0;">
          ${response.encodedImage ? `<img src="data:image/png;base64,${response.encodedImage}" alt="QR Code PIX" style="max-width: 300px; margin-bottom: 15px;" />` : ''}
          <textarea readonly style="width: 100%; min-height: 100px; padding: 10px; font-family: monospace; font-size: 12px;">${escaparHtml(response.payload)}</textarea>
        </div>
        <button type="button" id="copiar-pix" style="padding: 10px 20px; background: #1e7b85; color: white; border: none; border-radius: 5px; cursor: pointer;">
          Copiar Código PIX
        </button>`,
      width: 600,
      showConfirmButton: false,
      showCloseButton: true,
      didOpen: popup => {
        popup.querySelector('#copiar-pix')?.addEventListener('click', async e => {
          await navigator.clipboard.writeText(response.payload);
          e.currentTarget.textContent = 'Código PIX copiado!';
        });
      }
    });
  } catch (e) {
    Swal.fire('Erro', 'Erro ao obter PIX', 'error');
  }
};

export const abrirComprovanteFatura = async (api, fatura) => {
  try {
    const response = await api.get(`/faturas/${fatura.id}/comprovante`);
    if (!response || response.error) {
      Swal.fire('Aviso', response?.error || 'Comprovante não disponível para esta fatura', 'warning');
      return;
    }
    const botao = (url, cor, texto) => url
      ? `<a href="${escaparHtml(url)}" target="_blank" rel="noopener" style="display: inline-block; margin: 5px; padding: 10px 20px; background: ${cor}; color: white; text-decoration: none; border-radius: 5px;">${texto}</a>`
      : '';
    Swal.fire({
      title: 'Comprovante de Pagamento',
      html: `<div style="margin: 20px 0; text-align: left;">
        <p><strong>Data de Pagamento:</strong> ${response.paymentDate ? new Date(response.paymentDate).toLocaleDateString('pt-BR') : 'N/A'}</p>
        <p><strong>Valor Pago:</strong> ${moedaBRL(response.value)}</p>
        <div style="margin-top: 20px;">
          ${botao(response.transactionReceiptUrl, '#28a745', '📄 Ver Recibo')}
          ${botao(response.bankSlipUrl, '#007bff', '📋 Ver Boleto')}
          ${botao(response.invoiceUrl, '#6c757d', '📑 Ver Fatura')}
        </div></div>`,
      width: 600,
      confirmButtonText: 'Fechar'
    });
  } catch (e) {
    Swal.fire('Erro', 'Erro ao obter comprovante', 'error');
  }
};
